import { __toESM } from "../_virtual/_rolldown/runtime.js";
import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { require_prism } from "../node_modules/.pnpm/prismjs@1.30.0/node_modules/prismjs/prism.js";
import "../node_modules/.pnpm/prismjs@1.30.0/node_modules/prismjs/components/prism-typescript.js";
import "../node_modules/.pnpm/prismjs@1.30.0/node_modules/prismjs/components/prism-bash.js";
import "../node_modules/.pnpm/prismjs@1.30.0/node_modules/prismjs/components/prism-json.js";
import "../node_modules/.pnpm/prismjs@1.30.0/node_modules/prismjs/components/prism-yaml.js";
import "../node_modules/.pnpm/prismjs@1.30.0/node_modules/prismjs/components/prism-python.js";
import "../node_modules/.pnpm/prismjs@1.30.0/node_modules/prismjs/components/prism-java.js";
import "../node_modules/.pnpm/prismjs@1.30.0/node_modules/prismjs/components/prism-sql.js";
import "../node_modules/.pnpm/prismjs@1.30.0/node_modules/prismjs/components/prism-markdown.js";
import { LitElement, css, html, nothing } from "lit";
import { property, query, state } from "lit/decorators.js";
import { pdIcons } from "@progressive-development/pd-icon";
import "@progressive-development/pd-forms/pd-button";
//#region src/pd-code-snippet/PdCodeSnippet.ts
/**
* @license
* Copyright (c) 2024 PD Progressive Development UG. All rights reserved.
*/
var import_prism = /* @__PURE__ */ __toESM(require_prism(), 1);
/**
* Code snippet component with syntax highlighting.
*
* Displays code with proper syntax highlighting using Prism.js,
* with an optional title, copy-to-clipboard button, and source URL.
*
* @tagname pd-code-snippet
* @summary Code display component with syntax highlighting and copy functionality.
*
* @cssprop --pd-code-snippet-width - Width of the component. Default: `100%`.
* @cssprop --pd-code-snippet-border-radius - Border radius. Default: `--pd-radius-md`.
* @cssprop --pd-code-snippet-padding - Code padding. Default: `--pd-spacing-md`.
* @cssprop --pd-code-snippet-font-family - Code font family. Default: monospace.
* @cssprop --pd-code-snippet-font-size - Code font size. Default: `0.875rem`.
* @cssprop --pd-code-snippet-line-height - Code line height. Default: `1.5`.
* @cssprop --pd-code-snippet-bg-col - Code background color. Default: `--pd-default-bg-light-col`.
* @cssprop --pd-code-snippet-text-col - Code text color. Default: `--pd-default-font-content-col`.
* @cssprop --pd-code-snippet-header-bg-col - Header background. Default: `--pd-default-bg-dark-col`.
* @cssprop --pd-code-snippet-header-col - Header text color. Default: `--pd-default-font-col`.
* @cssprop --pd-code-snippet-url-col - URL link color. Default: `--pd-default-font-link-col`.
* @cssprop --pd-code-snippet-border-col - Border color. Default: `--pd-default-light-col`.
* @cssprop --pd-code-snippet-line-number-col - Line number color. Default: `--pd-default-font-muted-col`.
* @cssprop --pd-code-snippet-token-class - Syntax color for class names, regex, and variables. Default: `#267f99`.
* @cssprop --pd-code-snippet-token-comment - Syntax color for comments, prologs, and doctypes. Default: `#6a9955`.
* @cssprop --pd-code-snippet-token-function - Syntax color for functions and class-name tokens. Default: `#795e26`.
* @cssprop --pd-code-snippet-token-keyword - Syntax color for keywords, at-rules, and attr-values. Default: `#0550ae`.
* @cssprop --pd-code-snippet-token-number - Syntax color for numbers, constants, booleans, and symbols. Default: `#098658`.
* @cssprop --pd-code-snippet-token-operator - Syntax color for operators, entities, and URLs. Default: `#1e1e1e`.
* @cssprop --pd-code-snippet-token-property - Syntax color for property tokens. Default: `#0451a5`.
* @cssprop --pd-code-snippet-token-punctuation - Syntax color for punctuation. Default: `#1e1e1e`.
* @cssprop --pd-code-snippet-token-string - Syntax color for strings, selectors, and char tokens. Default: `#a31515`.
*
* @example
* ```html
* <pd-code-snippet
*   title="Installation"
*   language="bash"
*   url="https://docs.example.com/install"
* >
*   npm install @progressive-development/pd-content
* </pd-code-snippet>
* ```
*/
var PdCodeSnippet = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.title = "";
		this.language = "javascript";
		this.url = "";
		this.code = "";
		this.showLineNumbers = false;
		this.editable = false;
		this._editing = false;
		this._copied = false;
		this._slotCode = "";
		this._cmInitialized = false;
	}
	static {
		this.styles = [css`
      :host {
        /* Layout */
        --_width: var(--pd-code-snippet-width, 100%);
        --_radius: var(
          --pd-code-snippet-border-radius,
          var(--pd-radius-md, 4px)
        );
        --_padding: var(--pd-code-snippet-padding, var(--pd-spacing-md, 1rem));

        /* Typography */
        --_font-family: var(
          --pd-code-snippet-font-family,
          "Fira Code",
          "Consolas",
          "Monaco",
          monospace
        );
        --_font-size: var(--pd-code-snippet-font-size, 0.875rem);
        --_line-height: var(--pd-code-snippet-line-height, 1.5);

        /* Colors - Uses existing pd-default-* theme tokens with component-specific fallbacks */
        --_bg-col: var(
          --pd-code-snippet-bg-col,
          var(--pd-default-bg-light-col, #f2f9fa)
        );
        --_text-col: var(
          --pd-code-snippet-text-col,
          var(--pd-default-font-content-col, #353738)
        );
        --_header-bg-col: var(
          --pd-code-snippet-header-bg-col,
          var(--pd-default-bg-dark-col, #e3f2f5)
        );
        --_header-col: var(
          --pd-code-snippet-header-col,
          var(--pd-default-font-col, #3c3c3b)
        );
        --_url-col: var(
          --pd-code-snippet-url-col,
          var(--pd-default-font-link-col, #067394)
        );
        --_border-col: var(
          --pd-code-snippet-border-col,
          var(--pd-default-light-col, #afc1d2)
        );
        --_line-number-col: var(
          --pd-code-snippet-line-number-col,
          var(--pd-default-font-muted-col, #6b7280)
        );

        /* Prism syntax token colors - light theme defaults */
        --_token-comment: var(--pd-code-snippet-token-comment, #6a9955);
        --_token-keyword: var(--pd-code-snippet-token-keyword, #0550ae);
        --_token-string: var(--pd-code-snippet-token-string, #a31515);
        --_token-number: var(--pd-code-snippet-token-number, #098658);
        --_token-function: var(--pd-code-snippet-token-function, #795e26);
        --_token-class: var(--pd-code-snippet-token-class, #267f99);
        --_token-operator: var(--pd-code-snippet-token-operator, #1e1e1e);
        --_token-punctuation: var(--pd-code-snippet-token-punctuation, #1e1e1e);
        --_token-property: var(--pd-code-snippet-token-property, #0451a5);

        display: block;
        width: var(--_width);
      }

      .snippet-container {
        border-radius: var(--_radius);
        border: 1px solid var(--_border-col);
        overflow: hidden;
        background: var(--_bg-col);
      }

      /* Header */
      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.5rem var(--_padding);
        background: var(--_header-bg-col);
        border-bottom: 1px solid var(--_border-col);
        min-height: 2.5rem;
        box-sizing: border-box;
      }

      .title {
        margin: 0;
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--_header-col);
        font-family: var(--pd-default-font-content-family, inherit);
      }

      .language-badge {
        font-size: 0.75rem;
        color: var(--_header-col);
        opacity: 0.7;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .header-actions {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .header-actions pd-button {
        --pd-button-font-size: 0.75rem;
        --pd-button-height: 1.75rem;
      }

      /* Code area */
      .code-wrapper {
        overflow-x: auto;
        padding: var(--_padding);
      }

      pre {
        margin: 0;
        padding: 0;
        background: transparent;
        font-family: var(--_font-family);
        font-size: var(--_font-size);
        line-height: var(--_line-height);
        color: var(--_text-col);
        white-space: pre;
        word-wrap: normal;
        tab-size: 2;
      }

      code {
        font-family: inherit;
        background: transparent;
      }

      /* Line numbers */
      :host([showLineNumbers]) .code-wrapper {
        display: flex;
      }

      .line-numbers {
        flex-shrink: 0;
        padding-right: 1rem;
        margin-right: 1rem;
        border-right: 1px solid var(--_border-col);
        color: var(--_line-number-col);
        text-align: right;
        user-select: none;
        font-family: var(--_font-family);
        font-size: var(--_font-size);
        line-height: var(--_line-height);
      }

      /* URL footer */
      .footer {
        padding: 0.5rem var(--_padding);
        background: var(--_header-bg-col);
        border-top: 1px solid var(--_border-col);
      }

      .source-url {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.75rem;
        color: var(--_header-col);
      }

      .source-url a {
        color: var(--_url-col);
        text-decoration: none;
        word-break: break-all;
      }

      .source-url a:hover {
        text-decoration: underline;
      }

      .link-icon {
        width: 12px;
        height: 12px;
        fill: currentColor;
        flex-shrink: 0;
      }

      /* CodeMirror editor host */
      .cm-host {
        min-height: 120px;
      }

      .cm-host .cm-editor {
        outline: none;
      }

      .cm-host .cm-editor.cm-focused {
        outline: none;
      }

      /* Hidden slot for extracting text content */
      .hidden-slot {
        display: none;
      }

      /* Prism.js token styling */
      .token.comment,
      .token.prolog,
      .token.doctype,
      .token.cdata {
        color: var(--_token-comment);
      }

      .token.punctuation {
        color: var(--_token-punctuation);
      }

      .token.property,
      .token.tag,
      .token.boolean,
      .token.number,
      .token.constant,
      .token.symbol,
      .token.deleted {
        color: var(--_token-number);
      }

      .token.selector,
      .token.attr-name,
      .token.string,
      .token.char,
      .token.builtin,
      .token.inserted {
        color: var(--_token-string);
      }

      .token.operator,
      .token.entity,
      .token.url,
      .language-css .token.string,
      .style .token.string {
        color: var(--_token-operator);
      }

      .token.atrule,
      .token.attr-value,
      .token.keyword {
        color: var(--_token-keyword);
      }

      .token.function,
      .token.class-name {
        color: var(--_token-function);
      }

      .token.regex,
      .token.important,
      .token.variable {
        color: var(--_token-class);
      }
    `];
	}
	updated(changedProperties) {
		super.updated(changedProperties);
		if (this._editing) {
			this._initCodeMirror();
			if (this._cmEditor && changedProperties.has("code")) {
				const doc = this._cmEditor.state.doc.toString();
				if (this.code !== doc) this._cmEditor.dispatch({ changes: {
					from: 0,
					to: doc.length,
					insert: this.code
				} });
			}
		} else {
			this._destroyCodeMirror();
			if (changedProperties.has("code") || changedProperties.has("language") || changedProperties.has("_slotCode") || changedProperties.has("_editing")) this._highlightCode();
		}
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this._destroyCodeMirror();
	}
	render() {
		const lineCount = (this.code || this._slotCode).split("\n").length;
		return html`
      <div class="snippet-container">
        ${this.title || this.language !== "plain" || this.editable ? html`
                <div class="header">
                  <div class="title-area">
                    ${this.title ? html`<h4 class="title">${this.title}</h4>` : html`<span class="language-badge"
                            >${this.language}</span
                          >`}
                  </div>
                  <div class="header-actions">
                    ${this.title && this.language !== "plain" ? html`<span class="language-badge"
                            >${this.language}</span
                          >` : nothing}
                    ${this.editable ? html`
                            <pd-button
                              outline
                              text=""
                              icon="${pdIcons.ICON_EDIT_NEW}"
                              icon-label="${this._editing ? "Vorschau" : "Bearbeiten"}"
                              @button-clicked=${this._toggleEditing}
                            ></pd-button>
                          ` : nothing}
                    <pd-button
                      outline
                      text=""
                      icon="${pdIcons.ICON_COPY}"
                      icon-label="Code kopieren"
                      @button-clicked=${this._copyToClipboard}
                    ></pd-button>
                  </div>
                </div>
              ` : nothing}

        <div class="code-wrapper">
          ${this._editing ? html`<div class="cm-host"></div>` : html`
                  ${this.showLineNumbers ? html`
                          <div class="line-numbers" aria-hidden="true">
                            ${Array.from({ length: lineCount }, (_, i) => html`${i + 1}<br />`)}
                          </div>
                        ` : nothing}
                  <pre><code id="code-block"></code></pre>
                `}
        </div>

        ${this.url ? html`
                <div class="footer">
                  <div class="source-url">
                    <svg class="link-icon" viewBox="0 0 24 24">
                      <path
                        d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"
                      />
                    </svg>
                    <span>Source:</span>
                    <a
                      href="${this.url}"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ${this._formatUrl(this.url)}
                    </a>
                  </div>
                </div>
              ` : nothing}
      </div>

      <!-- Hidden slot to capture text content -->
      <div class="hidden-slot">
        <slot @slotchange=${this._handleSlotChange}></slot>
      </div>
    `;
	}
	/**
	* Extract code from slotted content.
	*/
	_handleSlotChange(e) {
		const text = e.target.assignedNodes({ flatten: true }).map((node) => node.textContent || "").join("").trim();
		if (text && text !== this._slotCode) this._slotCode = text;
	}
	/**
	* Apply syntax highlighting to the code.
	*/
	_highlightCode() {
		const codeBlock = this.shadowRoot?.getElementById("code-block");
		if (!codeBlock) return;
		const codeContent = this.code || this._slotCode;
		if (!codeContent) {
			codeBlock.textContent = "";
			return;
		}
		const grammar = this._getGrammar();
		if (grammar && this.language !== "plain") codeBlock.innerHTML = import_prism.default.highlight(codeContent, grammar, this.language);
		else codeBlock.textContent = codeContent;
	}
	/**
	* Get Prism grammar for the current language.
	*/
	_getGrammar() {
		const prismLang = {
			javascript: "javascript",
			typescript: "typescript",
			html: "html",
			css: "css",
			json: "json",
			bash: "bash",
			yaml: "yaml",
			python: "python",
			java: "java",
			sql: "sql",
			markdown: "markdown",
			plain: ""
		}[this.language];
		return prismLang ? import_prism.default.languages[prismLang] : null;
	}
	/**
	* Copy code to clipboard.
	*/
	async _copyToClipboard() {
		const codeContent = this.code || this._slotCode;
		try {
			await navigator.clipboard.writeText(codeContent);
			this._copied = true;
			setTimeout(() => {
				this._copied = false;
			}, 2e3);
		} catch (err) {
			console.error("Failed to copy code:", err);
		}
	}
	/**
	* Toggle between view and edit mode.
	*/
	_toggleEditing() {
		this._editing = !this._editing;
	}
	/**
	* Lazy-initialize CodeMirror editor.
	*/
	async _initCodeMirror() {
		if (this._cmInitialized || !this._cmHost) return;
		this._cmInitialized = true;
		const { createCodeMirrorEditor } = await import("./codemirror-setup.js");
		const codeContent = this.code || this._slotCode;
		this._cmEditor = createCodeMirrorEditor({
			parent: this._cmHost,
			code: codeContent,
			language: this.language,
			onChange: (newCode) => {
				this.code = newCode;
				this.dispatchEvent(new CustomEvent("pd-code-change", {
					detail: { code: newCode },
					bubbles: true,
					composed: true
				}));
			}
		});
	}
	/**
	* Destroy CodeMirror editor instance.
	*/
	_destroyCodeMirror() {
		if (this._cmEditor) {
			this._cmEditor.destroy();
			this._cmEditor = void 0;
			this._cmInitialized = false;
		}
	}
	/**
	* Format URL for display (truncate if too long).
	*/
	_formatUrl(url) {
		try {
			const parsed = new URL(url);
			const display = parsed.hostname + parsed.pathname;
			return display.length > 50 ? display.substring(0, 47) + "..." : display;
		} catch {
			return url.length > 50 ? url.substring(0, 47) + "..." : url;
		}
	}
};
__decorate([property({ type: String })], PdCodeSnippet.prototype, "title", void 0);
__decorate([property({ type: String })], PdCodeSnippet.prototype, "language", void 0);
__decorate([property({ type: String })], PdCodeSnippet.prototype, "url", void 0);
__decorate([property({ type: String })], PdCodeSnippet.prototype, "code", void 0);
__decorate([property({ type: Boolean })], PdCodeSnippet.prototype, "showLineNumbers", void 0);
__decorate([property({ type: Boolean })], PdCodeSnippet.prototype, "editable", void 0);
__decorate([state()], PdCodeSnippet.prototype, "_editing", void 0);
__decorate([state()], PdCodeSnippet.prototype, "_copied", void 0);
__decorate([state()], PdCodeSnippet.prototype, "_slotCode", void 0);
__decorate([query(".cm-host")], PdCodeSnippet.prototype, "_cmHost", void 0);
//#endregion
export { PdCodeSnippet };
